<template>
  <div class="d-flex flex-column w-100 h-100">
    <AgGridVue
      class="w-100 h-100"
      :column-defs="columnDefs"
      :row-data="clusterData"
      :get-row-id="getRowId"
      :grid-options="gridOptions"
      :components="components"
      :stop-editing-when-cells-loses-focus="true"
      @cell-value-changed="onChange"
      @grid-ready="onGridReady"
    />
    <PageActions
      :has-data-changes="hasDataChanges"
      :has-data-errors="hasDataErrors"
      @discard="discard"
      @save="onSave"
    />
  </div>
</template>

<script>
import agGridCellRendererComponentWrapper from '@/components/AgGridCellRendererComponentWrapper.vue';
import agGridIcon from '@/components/AgGridIcon.vue';
import { useDayjs } from '@/composables/use-dayjs';
import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';
import { useClusteringStore } from '@/stores/clustering';
import { useContextStore } from '@/stores/context';
import { useReferenceClusteringStore } from '@/stores/reference-clustering';
import { useScenariosStore } from '@/stores/scenarios';
import { useToolDataStore } from '@/stores/tool-data';
import agGridUtils from '@/utils/ag-grid-utils';
import {
  camelCase,
  cloneDeep,
  each,
  every,
  find,
  findIndex,
  get,
  groupBy,
  has,
  isEmpty,
  isEqual,
  isUndefined,
  keyBy,
  pick,
  reduce,
  size,
  some,
  toArray,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import iconTooltipRenderer from './IconTooltipRenderer.vue';

const SCENARIO = 'scenario-cluster';
const REFERENCE = 'reference-cluster';

export default {
  components: {
    /* eslint-disable vue/no-unused-components */
    agGridIcon,
    agGridCellRendererComponentWrapper,
    iconTooltipRenderer,
  },
  props: {
    mode: {
      type: String,
      required: true,
      default: 'scenario-cluster', // scenario-cluster or reference-cluster
    },
  },
  emits: ['open-cluster-generation-modal', 'data-changed', 'has-selected-rows', 'on-save'],

  setup() {
    const { canEditClusteringPage } = usePermission();
    const { formatDate } = useDayjs();
    const { tkey } = useInternationalization('clusteringPage.tabs.storeAllocation');

    return {
      canEditClusteringPage,
      formatDate,
      tkey,
    };
  },

  data() {
    return {
      gridApi: null,
      clusterData: null,
      storeKeyMap: null,
      storeKeysWithoutSales: new Set(),
      clusterWithoutSalesDataMap: {},
      gridOptions: {
        columnMenu: 'legacy',
        onSelectionChanged: this.onSelectionChanged,
        statusBar: {
          statusPanels: [{ statusPanel: 'agSelectedRowCountComponent', align: 'right' }],
        },
        enableGroupEdit: true,
        rowHeight: 30,
        suppressContextMenu: true,
        rowSelection: {
          mode: 'multiRow',
          // https://www.ag-grid.com/javascript-data-grid/grouping-row-selection/
          // when group is selected, only select the current values filtered
          groupSelects: 'filteredDescendants',
          // disables "select all" checkbox on column header
          headerCheckbox: false,
          // show checkboxes in the autoGroupColumn so they indent with data
          checkboxes: true,
          checkboxLocation: 'autoGroupColumn',
        },
        suppressRowClickSelection: true,
        autoGroupColumnDef: {
          headerName: 'Cluster',
          field: 'clusterName',
          minWidth: 250,
          cellRenderer: 'agGroupCellRenderer',
          cellRendererParams: {
            innerRenderer: 'iconTooltipRenderer',
            innerRendererParams: params => {
              const attributes = get(this.clusterAttributesMap, params.value, []);
              return {
                icon: 'info',
                hide: this.hideInfoIcon(params, attributes),
                messages: [this.tkey('regionalBrandsTooltipMessage'), attributes.join(', ')],
              };
            },
          },
          cellStyle: { width: '100%' },
          menuTabs: ['generalMenuTab', 'filterMenuTab'],
          sort: 'asc',
          editable: params => params.node.group && !this.isEditingDisabled,
        },
        tooltipShowDelay: 0,
        defaultColDef: {
          filter: true,
          sortable: true,
          comparator: agGridUtils.sortings.naturalSort,
          editable: false,
          menuTabs: ['filterMenuTab'],
          minWidth: 100,
          flex: 1,
          resizable: true,
          suppressMovable: true, // don't move columns, only rows
          tooltipValueGetter: () => {
            return this.hasDataErrors ? this.tkey('unclusteredError') : null;
          },
          cellClass: 'aligned-start priority-cell',
          cellClassRules: { 'invalid-nonempty': () => this.hasDataErrors },
        },
        suppressCellFocus: false,
      },
      components: null,
      modifiedClusters: new Set(),
    };
  },

  computed: {
    ...mapState(useClusteringStore, { selectedScenarioScheme: 'selectedScheme' }),
    ...mapState(useReferenceClusteringStore, { selectedReferenceScheme: 'selectedScheme' }),
    ...mapState(useScenariosStore, { scenarioStores: 'stores' }),
    ...mapState(useToolDataStore, { referenceStores: 'stores' }),
    ...mapState(useContextStore, ['getDateFormats', 'getClientConfig']),

    clusterAttributesMap() {
      return reduce(
        this.selectedScheme.clusters,
        (acc, c) => ({
          ...acc,
          ...(size(c.attributes) && { [c.clusterName]: c.attributes }),
        }),
        {}
      );
    },

    selectedScheme() {
      return this.mode === SCENARIO ? this.selectedScenarioScheme : this.selectedReferenceScheme;
    },

    stores() {
      return this.mode === SCENARIO ? this.scenarioStores : this.referenceStores;
    },

    setSelectedSchemeClusters() {
      return this.mode === SCENARIO
        ? this.setScenarioSelectedSchemeClusters
        : this.setReferenceSelectedSchemeClusters;
    },

    setSelectableClusterNames() {
      return this.mode === SCENARIO
        ? this.setScenarioSelectableClusterNames
        : this.setReferenceSelectableClusterNames;
    },

    updateClusters() {
      return this.mode === SCENARIO ? this.updateScenarioClusters : this.updateReferenceClusters;
    },

    hasDataChanges() {
      if (!this.clusterData) return false;
      const dataChanged = !isEqual(
        this.getStoreClusterMap(this.$options.initialClusterData),
        this.getStoreClusterMap(this.clusterData)
      );
      this.$emit('data-changed', dataChanged);
      return dataChanged;
    },

    hasDataErrors() {
      return this.uniqueClusterNames.size < 2;
    },

    hasSubClusteringEnabled() {
      return get(this.getClientConfig, 'features.subClusteringEnabled', false);
    },

    hasNoDataClustersInScheme() {
      return this.selectedScheme
        ? some(this.selectedScheme.clusters, ({ clusterName }) => this.isNoDataCluster(clusterName))
        : false;
    },

    uniqueClusterNames() {
      return this.clusterData ? new Set(this.clusterData.map(c => c.clusterName)) : new Set();
    },

    isEditingDisabled() {
      return !this.canEditClusteringPage;
    },

    columnDefs() {
      const columns = [
        {
          field: 'storeWarningIcon',
          headerName: '',
          maxWidth: 30,
          sortable: false,
          editable: false,
          menuTabs: [],
          cellClass: 'justify-center',
          cellStyle: { padding: '0' },
          cellRenderer: 'agGridCellRendererComponentWrapper',
          valueGetter: params => ({
            component: 'error-triangle',
            props: {
              errors: this.getRowWarning(params),
              iconType: 'warning_amber',
              small: true,
              round: true,
            },
          }),
        },
        {
          headerName: this.tkey('tableHeaders.clusterName'),
          field: 'clusterName',
          sort: 'asc',
          rowGroup: true,
          hide: true,
          editable: false, // AOV3-1128 TODO: edit groups but not children
        },
        {
          headerName: this.tkey('tableHeaders.storeKeyDisplay'),
          field: 'storeKeyDisplay',
        },
        {
          headerName: this.tkey('tableHeaders.formatDescription'),
          field: 'formatDescription',
        },
        {
          headerName: this.tkey('tableHeaders.storeDescription'),
          field: 'storeDescription',
        },
        {
          headerName: this.tkey('tableHeaders.storeProfile'),
          field: 'storeProfile',
        },
        {
          headerName: this.tkey('tableHeaders.country'),
          field: 'country',
        },
        {
          headerName: this.tkey('tableHeaders.province'),
          field: 'province',
        },
        {
          headerName: this.tkey('tableHeaders.city'),
          field: 'city',
        },
        {
          headerName: this.tkey('tableHeaders.openDate'),
          field: 'openDate',
          valueFormatter: this.dateFormatter,
          filterParams: { valueFormatter: this.dateFormatter }, // format values in filter same as in column
        },
        {
          headerName: this.tkey('tableHeaders.closeDate'),
          field: 'closeDate',
          valueFormatter: this.dateFormatter,
          filterParams: { valueFormatter: this.dateFormatter },
        },
      ];
      if (this.mode === SCENARIO) {
        columns.push(
          ...[
            {
              headerName: this.tkey('tableHeaders.analysis'),
              field: 'analysis',
              cellRenderer: params => String(params.value ?? ''),
            },
            {
              headerName: this.tkey('tableHeaders.assortment'),
              field: 'assortment',
              cellRenderer: params => String(params.value ?? ''),
            },
          ]
        );
      }
      if (this.mode === SCENARIO && this.hasSubClusteringEnabled) {
        // Add split icon column
        columns.unshift({
          field: 'subClusterIcon',
          headerName: '',
          maxWidth: 30,
          sortable: false,
          editable: false,
          resizable: false,
          menuTabs: [],
          cellClass: 'justify-center clickable sub-clustering-icon',
          cellStyle: { padding: '0' },
          cellRenderer: params =>
            agGridUtils.utils.customIconRenderer(params, {
              icon: 'alt_route',
              isDisplayed: !!params.node.group,
            }),
          onCellClicked: params => this.$emit('open-cluster-generation-modal', params),
        });
      }

      const extraColumns = get(this.getClientConfig, 'storeColumns', {});

      return agGridUtils.builders.mergeHeaders(columns, extraColumns, {
        componentName: camelCase(this.$options.name),
      });
    },
  },

  async created() {
    this.storeKeyMap = keyBy(this.stores, 'storeKey');
    this.init();
    this.updateNoDataClusters();
  },

  beforeUnmount() {
    this.$emit('data-changed', false);
  },

  methods: {
    ...mapActions(useClusteringStore, {
      setScenarioSelectableClusterNames: 'setSelectableClusterNames',
      setScenarioSelectedSchemeClusters: 'setSelectedSchemeClusters',
      updateScenarioClusters: 'updateClusters',
    }),
    ...mapActions(useReferenceClusteringStore, {
      setReferenceSelectableClusterNames: 'setSelectableClusterNames',
      setReferenceSelectedSchemeClusters: 'setSelectedSchemeClusters',
      fetchClusterScheme: 'fetchClusterScheme',
      updateReferenceClusters: 'updateClusters',
    }),

    onGridReady(params) {
      this.gridApi = params.api;
      this.gridApi.setColumnsVisible(['storeWarningIcon'], this.hasNoDataClustersInScheme);

      this.$emit('has-selected-rows', !isEmpty(this.gridApi.getSelectedRows()));
    },

    init() {
      if (this.mode === SCENARIO) this.setStoresWithoutSalesData();
      this.$options.initialClusterData = cloneDeep(this.getStoresGroupedByScheme());
      this.clusterData = cloneDeep(this.$options.initialClusterData);
    },
    getRowId(row) {
      const { data } = row;
      return String(data.storeKey);
    },

    onChange(params) {
      // refresh grouping when clusterName changes, handles new group name or moving to existing group with edit
      if (params.node.group) this.moveChildrenToGroup(params);

      this.refresh();
    },

    refresh() {
      // update row grouping
      this.gridApi.refreshClientSideRowModel();

      // may have removed all rows from group, essentially deleting it,
      // so we need to refresh selectable cluster names in the dropdown
      this.setSelectableClusterNames(this.uniqueClusterNames);
    },

    isNoDataCluster(clusterName) {
      return this.clusterWithoutSalesDataMap[clusterName];
    },

    updateNoDataClusters() {
      const storesByCluster = groupBy(this.clusterData, 'clusterName');
      this.clusterWithoutSalesDataMap = {}; // refresh data before every update
      if (this.mode === SCENARIO) {
        // check if all stores in a cluster are missing sales data
        each(storesByCluster, (stores, cluster) => {
          // AOV3-1490 workaround for stores that don't have hasPerformancePeriodSalesData flag:
          // If stores are not flagged and cluster is already mapped as no data, add it to this map
          // It is sufficient to check only first store for the presence of the flag.
          if (!has(stores[0], 'hasPerformancePeriodSalesData')) {
            const clusterInfo = find(this.selectedScheme.clusters, ({ clusterName }) =>
              isEqual(clusterName, cluster)
            );
            if (clusterInfo) this.clusterWithoutSalesDataMap[cluster] = clusterInfo.isNoDataCluster;
          } else {
            this.clusterWithoutSalesDataMap[cluster] = every(stores, s =>
              this.storeKeysWithoutSales.has(s.storeKey)
            );
          }
        });

        // update isNoDataCluster key for each table row
        each(this.clusterData, cd => {
          cd.isNoDataCluster = this.clusterWithoutSalesDataMap[cd.clusterName];
        });
      }
      // Keep clusters state up to date
      this.setSelectedSchemeClusters(this.formatClusterData());
    },

    getRowWarning(params) {
      if (params.node.group) {
        // negate as error state is when value is false
        return {
          [this.tkey('noDataClusterWarningMessage')]: !this.isNoDataCluster(params.node.key),
        };
      }

      const { hasPerformancePeriodSalesData, assortment } = params.data;

      return {
        [this.tkey('noSalesStoreWarningMessage')]:
          isUndefined(hasPerformancePeriodSalesData) || hasPerformancePeriodSalesData, // can be undefined for old WPs, AOV3-1435 had no migration
        [this.tkey('notInAssortmentWarningMessage')]: assortment,
      };
    },

    onSelectionChanged() {
      this.$emit('has-selected-rows', !isEmpty(this.gridApi.getSelectedRows()));
    },

    getStoreClusterMap(rows = []) {
      return reduce(
        rows,
        (acc, row) => {
          acc[row.storeKey] = row.clusterName;
          return acc;
        },
        {}
      );
    },

    formatClusterData() {
      const clusterObj = {};
      // group each store row from ag-grid by their cluster
      this.clusterData.forEach(
        ({
          clusterName,
          clusterId,
          attributes,
          storeKey,
          isNoDataCluster,
          usesUnclusteredMatrix,
          calculationMethod,
        }) => {
          // Exclude attributes from cluster data if cluster has been modified
          const includeAttributes = attributes && !this.modifiedClusters.has(clusterName);
          // can't use clusterId, will be undefined for new clusters
          if (get(clusterObj, clusterName)) {
            clusterObj[clusterName].storeKeys.push(storeKey);
          } else {
            clusterObj[clusterName] = {
              clusterName,
              clusterId,
              ...(includeAttributes && { attributes }),
              isNoDataCluster,
              usesUnclusteredMatrix,
              storeKeys: [storeKey],
              calculationMethod,
            };
          }
        }
      );
      const newClusters = toArray(clusterObj);
      const hasDifferentClustersCount =
        this.selectedScheme.clusterCount !== toArray(clusterObj).length;

      // if user manually generates new clusters, we reset its ids to ensure they are unique
      if (hasDifferentClustersCount) {
        newClusters.forEach(c => {
          c.clusterId = null;
        });
      }

      return newClusters;
    },

    getStoresGroupedByScheme() {
      const tableRows = reduce(
        this.selectedScheme.clusters,
        (acc, c) => {
          const {
            clusterName,
            clusterId,
            attributes,
            isNoDataCluster,
            usesUnclusteredMatrix,
            calculationMethod,
          } = c;
          // for each store from each cluster in scheme, create an entry which will represent a table row
          each(c.storeKeys, storeKey => {
            if (this.storeKeyMap[storeKey] !== undefined) {
              const store = pick(this.storeKeyMap[storeKey], [
                'storeKey', // internal use, not displayed
                'storeKeyDisplay',
                'formatDescription',
                'storeDescription',
                'storeProfile',
                'country',
                'province',
                'city',
                'openDate',
                'closeDate',
                'analysis',
                'assortment',
                'storeStatus',
                'hasPerformancePeriodSalesData',
                'distributionChannel',
              ]);

              acc.push({
                clusterName,
                clusterId,
                attributes,
                isNoDataCluster,
                usesUnclusteredMatrix,
                calculationMethod,
                ...store,
              });
            }
          });
          return acc;
        },
        []
      );
      if (this.mode === REFERENCE) {
        tableRows.push(...this.setUnassignedStores());
      }
      return tableRows;
    },

    setStoresWithoutSalesData() {
      each(this.stores, s => {
        const hasMissingSalesData =
          has(s, 'hasPerformancePeriodSalesData') && !s.hasPerformancePeriodSalesData;
        if (!s.analysis || !s.assortment || hasMissingSalesData) {
          this.storeKeysWithoutSales.add(s.storeKey);
        }
      });
    },

    setUnassignedStores() {
      const cluster = {
        clusterName: 'Unassigned',
        clusterId: -1,
        isNoDataCluster: false,
        usesUnclusteredMatrix: false,
      };
      // get all the storeKeys currently assigned
      const assignedStoreKeys = [];
      this.selectedScheme.clusters.forEach(c => {
        assignedStoreKeys.push(...c.storeKeys);
      });

      const unAssignedStores = [];
      Object.entries(this.storeKeyMap).forEach(([key, storeValue]) => {
        const storeKey = Number(key);
        const assignedIndex = findIndex(assignedStoreKeys, a => {
          return a === storeKey;
        });
        if (assignedIndex < 0) {
          const store = pick(storeValue, [
            'storeKey', // internal use, not displayed
            'storeKeyDisplay',
            'formatDescription',
            'storeDescription',
            'storeProfile',
            'country',
            'province',
            'city',
            'openDate',
            'closeDate',
            'analysis',
            'assortment',
            'storeStatus',
            'hasPerformancePeriodSalesData',
            'distributionChannel',
          ]);

          unAssignedStores.push({
            ...cluster,
            ...store,
          });
        }
      });
      return unAssignedStores;
    },

    moveChildrenToGroup(params) {
      const groupClusterName = params.data.clusterName;
      const movingRows = params.node.allLeafChildren.map(c => c.data);
      this.moveRowsToGroup({ groupClusterName, movingRows });
    },

    moveRowsToGroup({ groupClusterName, movingRows }) {
      // used from clustering.vue, when value in v-select changes
      if (isEmpty(movingRows)) {
        movingRows = this.gridApi.getSelectedRows();
      }
      const movingStoreKeys = new Set(movingRows.map(row => row.storeKey));
      const modifiedClusters = reduce(
        movingRows,
        (acc, row) => {
          if (row.clusterName !== groupClusterName) acc.add(row.clusterName);
          return acc;
        },
        new Set()
      );
      if (modifiedClusters.size) {
        this.modifiedClusters.add(groupClusterName);
        const clusterToMoveStoreTo = find(this.selectedScheme.clusters, {
          clusterName: groupClusterName,
        });
        const isMovingToNewCluster =
          groupClusterName === this.$t('clusteringPage.newCluster') && !clusterToMoveStoreTo;

        const rowsToUpdate = [];
        this.clusterData.forEach(row => {
          if (!movingStoreKeys.has(row.storeKey)) return;

          this.modifiedClusters.add(row.clusterName);
          row.clusterName = groupClusterName;
          row.clusterId = isMovingToNewCluster
            ? null
            : get(clusterToMoveStoreTo, 'clusterId', row.clusterId);
          rowsToUpdate.push(row);
        });

        this.gridApi.applyTransaction({ update: rowsToUpdate });
      }
      this.gridApi.clearFocusedCell();
      this.updateNoDataClusters();
      this.refresh();
    },

    dateFormatter(params) {
      if (params.node.group) return;
      return this.formatDate(params.value, this.getDateFormats.long);
    },

    hideInfoIcon(params, attributes) {
      return (
        !this.hasRegionsByAttributeEnabled ||
        params.node.level ||
        !size(attributes) ||
        this.modifiedClusters.has(params.value)
      );
    },

    async discard() {
      if (this.mode === REFERENCE) {
        await this.fetchClusterScheme(); // reloads from database
      }
      if (this.mode === SCENARIO) this.setStoresWithoutSalesData();
      this.clusterData = cloneDeep(this.$options.initialClusterData);
      // Reset overall list of modified clusters as they have been returned to original state
      this.modifiedClusters = new Set();
      this.gridApi.refreshCells();
      this.refresh();
      this.updateNoDataClusters();
    },

    async onSave() {
      await this.$emit('on-save');
    },

    async onSaveRefresh() {
      await this.updateClusters(this.formatClusterData());
      this.gridApi.deselectAll();
      this.$options.initialClusterData = cloneDeep(this.clusterData);
      // hacky way to force 'hasDataChanges' computed to recalculate,
      // since it is not reacting to this.$options.initialClusterData change
      this.clusterData = cloneDeep(this.$options.initialClusterData);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

:deep(.ag-row-group) {
  background: $assortment-table-white-bg-colour;
}

:deep(.ag-row-selected),
:deep(.ag-row-selected .ag-row-group) {
  background: $assortment-table-blue-bg-colour !important;

  &:before {
    content: none;
  }
}

:deep(.sub-clustering-icon i) {
  transform: rotate(90deg) scale(90%);
  color: $assortment-sub-clustering-button-background-color;
}

.clickable {
  &:hover {
    cursor: pointer;
  }
}
</style>
